import React, { useState, useEffect } from 'react';

import { useSelectedDevice } from '../../services/DeviceService/DevicesHooks';

import { useGlobalStore } from '../../services/GlobalStoreService/GlobalStoreHooks';

import { Button, Accordion, Spinner, Icon } from 'selfly-ui-react';

import { JOKI } from '../../JOKI';

import keyExists from '../../utils/keyExists';
import { toHumanReadableDate } from '../../utils/dateTools';
import deviceTypeIdToText from '../../utils/deviceTypeIdToText';

import DeviceStatusIndicator from '../DeviceCommons/DeviceStatusIndicator/DeviceStatusIndicator';
import DeviceIcon from '../DeviceCommons/DeviceIcon/DeviceIcon';
import DeviceAlarmTable from '../DeviceCommons/DeviceAlarmTable/DeviceAlarmTable';
import DeviceHealthStatus from './DeviceHealthStatus/DeviceHealthStatus';
import DeviceReaderAndAntenna from './DeviceReaderAndAntenna/DeviceReaderAndAntenna';
import DeviceDockerTable from './DeviceDockerTable/DeviceDockerTable';
import DeviceManagementActions from './DeviceManagementActions/DeviceManagementActions';
import DeviceTestResults from './DeviceTestResults/DeviceTestResults';

import DeviceMetaEditor from './DeviceMetaEditor/DeviceMetaEditor';

import './device-details.scss';

export default function DeviceDetails(props) {
    const [device, selectionState, changeSelection] = useSelectedDevice();

    const [creatingAlert, setCreatingAlert] = useState(false);

    const [mode, setMode] = useGlobalStore('mode', 'READ');

    const permissions = props.permissions;

    // If total alert trigger sum of current device is larget than the one stored into creatingAlert, set the creatingAlert to false.
    // This will activate the test alert button again.
    useEffect(() => {
        if (device !== null && Array.isArray(device.alerts) && process.env.NODE_ENV === 'development') {
            if (typeof creatingAlert === 'number') {
                const totalActiveAlertCount = device.alerts.reduce((tot, alert) => {
                    if (alert.state === 'ACTIVE') {
                        tot += alert.triggerCounter;
                    }
                    return tot;
                }, 0);

                if (totalActiveAlertCount > creatingAlert) {
                    setCreatingAlert(false);
                }
            }
        }
    }, [device, creatingAlert]);

    // If mode is changed to EDIT stop the devices updater.
    useEffect(() => {
        if (mode === 'READ') {
            JOKI.trigger({
                from: 'React:DeviceDetails',
                to: 'DevicesService',
                key: 'updateIntervalSpeed',
                body: 'restart',
            });
        }

        if (mode === 'EDIT') {
            JOKI.trigger({
                from: 'React:DeviceDetails',
                to: 'DevicesService',
                key: 'updateIntervalSpeed',
                body: 'stop',
            });
        }
    }, [mode]);

    if (selectionState === 'NOT') {
        return null;
    }

    if (selectionState === 'ERROR') {
        return (
            <div className="body-details device-details">
                <header className="device-details-header">
                    <div className="device-detail-status error">ERROR</div>
                    <div className="device-detail-close">
                        <Button icon="close" onClick={close} />
                    </div>
                </header>

                <div>{device.msg}</div>
            </div>
        );
    }

    if (selectionState === 'LOADING') {
        return (
            <div className="body-details device-details">
                <header className="device-details-header">
                    <div className="device-detail-status loading">Loading</div>
                    <div className="device-detail-close">
                        <Button icon="close" onClick={close} />
                        <Spinner />
                    </div>
                </header>
            </div>
        );
    }

    function close() {
        if (mode !== 'READ') {
            setMode('READ');
        }
        changeSelection(null);
    }

    function refesh() {
        JOKI.trigger({
            to: 'DevicesService',
            key: 'forceDeviceUpdate',
            body: {
                code: device.code,
            },
        });
    }

    function edit() {
        setMode('EDIT');
    }

    /**
     * This function creates a new test alert for this device that can be used to test manual cancelling for example
     */
    function createTestAlert() {
        // Count the total triggerCounter sum of all active alerts and store it to creatingAlert
        const totalActiveAlertCount = device.alerts.reduce((tot, alert) => {
            if (alert.state === 'ACTIVE') {
                tot += alert.triggerCounter;
            }
            return tot;
        }, 0);

        setCreatingAlert(totalActiveAlertCount);

        JOKI.trigger({
            to: 'AlertsService',
            key: 'createTestAlert',
            body: {
                device: device,
            },
        });
    }

    function findOnMap() {
        JOKI.trigger({
            key: 'moveMapToCoords',
            body: device.metadata.values,
        });
    }

    const activeAlarms = device.alerts !== undefined && Array.isArray(device.alerts) ? device.alerts.filter(alert => alert.state !== 'RESOLVED') : [];

    const alarmHistory = device.alerts !== undefined && Array.isArray(device.alerts) ? device.alerts.filter(alert => alert.state === 'RESOLVED') : [];

    const dockers =
        keyExists('currentState.raw.docker', device) && device.currentState.raw.docker !== null && Array.isArray(device.currentState.raw.docker)
            ? device.currentState.raw.docker
            : [];

    const dataStatus = device.currentState !== undefined;

    const activeAlertText = device.alertCount
        ? `${device.alertCount.active.oosSeverity}/${device.alertCount.active.urgentSeverity}/${device.alertCount.active.criticalSeverity}`
        : '0';
    return (
        <div className="body-details device-details">
            <header className="device-details-header">
                <div className="device-detail-status">
                    <DeviceStatusIndicator device={device} large />
                </div>
                <div className="device-detail-icon">
                    <DeviceIcon device={device} large />
                </div>

                <div className="device-detail-name">
                    <h1>{device.name}</h1>
                </div>

                <div className="device-detail-id">
                    <h2>Id: {device.id}</h2>
                    <h2>Code: {device.code}</h2>
                    <p>{deviceTypeIdToText(device.type)}{' / '}{device.subType && device.subType != 'None' ? device.subType:''}</p>
                    <p></p>
                </div>

                <div className="device-detail-actions">
                    {mode === 'READ' && process.env.NODE_ENV === 'development' && (
                        <Button
                            icon="notifications_active"
                            onClick={createTestAlert}
                            id="ButtonCreateTestAlarm"
                            type="negative"
                            disabled={creatingAlert !== false}
                        />
                    )}
                    {mode === 'READ' && permissions.includes('deviceManagement') && dataStatus && <Button icon="edit" onClick={edit} id="ButtonEditDeviceMetaDetails" />}
                    {mode === 'READ' && <Button icon="refresh" onClick={refesh} id="ButtonRefreshDeviceDetails" />}
                    {keyExists('metadata.raw.coordinates_lat', device) && <Button icon="location_searching" onClick={findOnMap} id="ButtonFindDeviceOnMap" />}
                    <Button icon="close" onClick={close} id="ButtonCloseDeviceDetails" />

                    {selectionState === 'CHANGING' && <Spinner />}
                </div>

                <div className="device-detail-loader">
                    {dataStatus === false && (
                        <div className="missing-details">
                            <Icon size="lg">warning</Icon>
                            <label>Details missing or loading...</label>
                        </div>
                    )}
                </div>
            </header>

            {mode === 'READ' && (
                <React.Fragment>
                    {device.description !== undefined && device.description !== null && device.description !== '' && (
                        <div className="device-details-description">
                            <label>Description</label>
                            <p>{device.description}</p>
                        </div>
                    )}

                    <div className="device-details-info">
                        <div>
                            <label>Last time seen online</label>
                            <p>{toHumanReadableDate(device.lastTimeSeenOnline)}</p>
                        </div>
                        <div>
                            <label>Coordinates Lat/Lon</label>
                            <p>
                                {keyExists('metadata.raw.coordinates_lat', device) ? device.metadata.raw.coordinates_lat : '- '},{' '}
                                {keyExists('metadata.raw.coordinates_lon', device) ? device.metadata.raw.coordinates_lon : '-'}
                            </p>
                        </div>
                        <div>
                            <label>Address</label>

                            {keyExists('metadata.raw.address', device) ? (
                                device.metadata.raw.address !== null ? (
                                    <AddressDisplay address={device.metadata.raw.address} />
                                ) : null
                            ) : null}
                        </div>
                    </div>

                    <Accordion title={`Active Alarms (${activeAlertText})`} icon="alarm">
                        <DeviceAlarmTable alarms={activeAlarms} cancelAlarms={true} device={device} />
                    </Accordion>

                    <Accordion title={`Alarms in last 2 weeks (${alarmHistory.length})`} icon="alarm_off">
                        <DeviceAlarmTable alarms={alarmHistory} cancelAlarms={false} device={device} />
                    </Accordion>

                    {permissions.includes('deviceManagement') && (
                        <Accordion title="Device Management Actions" icon="build">
                            <DeviceManagementActions device={device} permissions={permissions}/>
                        </Accordion>
                    )}

                    <Accordion title="Device Health Status" icon="show_chart">
                        <DeviceHealthStatus device={device} />
                    </Accordion>

                    {permissions.includes('organizationCRUD') && (
                        <Accordion title="Device Reader and Antenna" icon="settings_input_antenna">
                            <DeviceReaderAndAntenna device={device} />
                        </Accordion>
                    )}

                    {permissions.includes('organizationCRUD') && (
                        <Accordion title={`Device SW Status (${dockers.length})`} icon="cloud">
                            <DeviceDockerTable dockers={dockers} />
                        </Accordion>
                    )}
                    {permissions.includes('organizationCRUD') && (
                        <Accordion title="Factory Acceptance Test Results" icon="thumbs_up_down">
                            <DeviceTestResults device={device} />
                        </Accordion>
                    )}
                </React.Fragment>
            )}
            {mode === 'EDIT' && <DeviceMetaEditor device={device} />}
        </div>
    );
}

function AddressDisplay(props) {
    const { street, street2, city, zipcode, state, country } = props.address;

    const addressKeys = Object.keys(props.address).filter(key => props.address[key] !== '');

    if (addressKeys.length === 0) {
        return null;
    }

    return (
        <p>
            {street !== '' ? street : null}
            {street !== '' ? <br /> : null}
            {street2 !== '' ? street2 : null}
            {street2 !== '' ? <br /> : null}
            {zipcode !== '' ? zipcode : null} {city !== '' ? city : null}
            {zipcode !== '' || city !== '' ? <br /> : null}
            {state !== '' ? state : null}
            {state !== '' ? <br /> : null}
            {country !== '' ? country : null}
        </p>
    );
}
